import { useEffect, useRef, useState, type FormEvent } from "react";
import { Check, Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { authApi } from "../../api/auth";
import type { EnrollmentStartResponse } from "../../api/types";
import { Button, CodeInput, ErrorText, Field, FullScreenLoader, TextInput } from "../../components/ui";

interface StepProps {
  onError: (err: unknown) => string;
}

export interface Remember { remember?: boolean; setRemember?: (v: boolean) => void }

function RememberBox({ remember, setRemember }: Remember) {
  if (!setRemember) return null;
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-300">
      <input type="checkbox" checked={!!remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-[#ff1d00]" data-testid="admin-login-remember-device" />
      Remember this device for 30 days
    </label>
  );
}

export function TotpStep({ onDone, onError, onUseBackup, remember, setRemember }: StepProps & Remember & { onDone: () => void; onUseBackup: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (value = code) => {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await authApi.verifyTotp(value, !!remember);
      onDone();
    } catch (err) {
      setError(onError(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="space-y-6" data-testid="admin-totp-form">
      <CodeInput value={code} onChange={setCode} onComplete={(v) => void submit(v)} testId="admin-totp-input" autoFocus />
      <RememberBox remember={remember} setRemember={setRemember} />
      <ErrorText message={error} testId="admin-totp-error" />
      <Button type="submit" loading={busy} disabled={code.length !== 6} className="w-full" data-testid="admin-totp-submit-button">
        Verify
      </Button>
      <Button type="button" variant="ghost" onClick={onUseBackup} className="w-full" data-testid="admin-use-backup-code-button">
        <KeyRound className="h-4 w-4" /> Use a backup code instead
      </Button>
    </form>
  );
}

export function BackupCodeStep({ onDone, onError, onBack, remember, setRemember }: StepProps & Remember & { onDone: () => void; onBack: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authApi.verifyBackupCode(code.trim(), !!remember);
      toast.success("Backup code accepted. It can't be used again.");
      onDone();
    } catch (err) {
      setError(onError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="admin-backup-code-form">
      <Field label="Backup code" htmlFor="backup-code">
        <TextInput id="backup-code" placeholder="XXXXX-XXXXX" autoComplete="off" value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())} className="font-mono tracking-widest"
          data-testid="admin-backup-code-input" />
      </Field>
      <RememberBox remember={remember} setRemember={setRemember} />
      <ErrorText message={error} testId="admin-backup-code-error" />
      <Button type="submit" loading={busy} disabled={code.trim().length < 8} className="w-full" data-testid="admin-backup-code-submit-button">
        Verify backup code
      </Button>
      <Button type="button" variant="ghost" onClick={onBack} className="w-full" data-testid="admin-backup-back-button">
        Back to authenticator code
      </Button>
    </form>
  );
}

export function EnrollStep({ onDone, onError }: StepProps & { onDone: (codes: string[]) => void }) {
  const [setup, setSetup] = useState<EnrollmentStartResponse | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    authApi.startEnrollment().then(setSetup).catch((err) => setError(onError(err)));
  }, [onError]);

  const submit = async (value = code) => {
    if (value.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authApi.completeEnrollment(value);
      onDone(res.backup_codes);
    } catch (err) {
      setError(onError(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  if (!setup) return error ? <ErrorText message={error} testId="admin-enroll-error" /> : <FullScreenLoader />;

  return (
    <div className="space-y-8" data-testid="admin-enroll-step">
      <ol className="space-y-2 text-sm text-zinc-300">
        <li>1. Open Google Authenticator, 1Password, Authy or similar.</li>
        <li>2. Scan the QR code, or enter the setup key manually.</li>
        <li>3. Enter the 6-digit code it shows.</li>
      </ol>
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <img src={setup.qr_svg_data_uri} alt="Authenticator QR code" data-testid="admin-enroll-qr-code"
          className="h-44 w-44 shrink-0 rounded-sm border border-white/10 bg-snow p-2" />
        <div className="min-w-0 space-y-2">
          <p className="label-caps text-zinc-400">Setup key</p>
          <p data-testid="admin-enroll-manual-key" className="break-all font-mono text-sm tracking-widest text-white">
            {setup.secret.match(/.{1,4}/g)?.join(" ")}
          </p>
          <CopyButton value={setup.secret} testId="admin-enroll-copy-key-button" label="Copy key" />
        </div>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="space-y-6">
        <CodeInput value={code} onChange={setCode} onComplete={(v) => void submit(v)} testId="admin-enroll-code-input" />
        <ErrorText message={error} testId="admin-enroll-error" />
        <Button type="submit" loading={busy} disabled={code.length !== 6} className="w-full" data-testid="admin-enroll-verify-button">
          Verify and enable 2FA
        </Button>
      </form>
    </div>
  );
}

function CopyButton({ value, testId, label }: { value: string; testId: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed. Select the text manually.");
    }
  };
  return (
    <Button type="button" variant="secondary" onClick={copy} className="h-9 px-3 text-xs" data-testid={testId}>
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : label}
    </Button>
  );
}

export function BackupCodesReveal({ codes, onContinue }: { codes: string[]; onContinue: () => void }) {
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div className="space-y-6" data-testid="admin-backup-codes-reveal">
      <ul className="grid grid-cols-2 gap-2" data-testid="admin-backup-codes-list">
        {codes.map((c, i) => (
          <li key={c} data-testid={`admin-backup-code-${i}`} className="rounded-sm border border-white/10 bg-surface px-3 py-2 text-center font-mono text-sm tracking-widest text-white">
            {c}
          </li>
        ))}
      </ul>
      <CopyButton value={codes.join("\n")} testId="admin-backup-codes-copy-button" label="Copy all codes" />
      <label className="flex cursor-pointer items-start gap-3 text-sm text-zinc-300">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-[#ff1d00]" data-testid="admin-backup-codes-confirm-checkbox" />
        I have saved these codes. I understand they will not be shown again.
      </label>
      <Button onClick={onContinue} disabled={!confirmed} className="w-full" data-testid="admin-backup-codes-continue-button">
        Continue to dashboard
      </Button>
    </div>
  );
}
